/**
 * Synapse-related API helpers.
 * Accepts the shared apiClient instance so we avoid circular deps.
 */
export const createSynapseService = (apiClient) => {
  if (!apiClient) {
    throw new Error('apiClient instance is required to create Synapse service');
  }

  return {
    /**
     * Fetch synapses for a graph.
     * @param {Object} options
     * @param {string} options.graphId - Graph identifier (required)
     * @param {number} [options.page=1] - Page number
     * @param {number} [options.pageSize=25] - Records per page
     * @returns {Promise<Object>} API response payload
     */
    listSynapses: async ({ graphId, page = 1, pageSize = 25, query }) => {
      if (!graphId) {
        throw new Error('graphId is required to list synapses');
      }

      const params = {
        graphId,
        page,
        pageSize,
      };

      if (typeof query === 'string' && query.trim() !== '') {
        params.query = query.trim();
      }

      const response = await apiClient.get('/edges', {
        params,
      });
      return response.data;
    },

    /**
     * Retrieve a single connection by ID.
     * @param {string} synapseId
     * @returns {Promise<Object>}
     */
    getSynapse: async (synapseId) => {
      if (!synapseId) {
        throw new Error('connection id is required to fetch connection details');
      }
      const response = await apiClient.get(`/edges/${synapseId}`);
      return response.data;
    },

    /**
     * Update connection metadata (description, weight, delay, attributes).
     * @param {string} synapseId
     * @param {Object} update
     * @returns {Promise<Object>}
     */
    updateSynapse: async (synapseId, update) => {
      if (!synapseId) {
        throw new Error('connection id is required to update connection details');
      }
      const response = await apiClient.put(`/edges/${synapseId}`, update);
      return response.data;
    },

    /**
     * Create a new synapse connection.
     * @param {Object} synapseData
     * @param {string} synapseData.graphId - Graph identifier (required)
     * @param {string} synapseData.sourceNodeId - Source node identifier (required)
     * @param {string} synapseData.targetNodeId - Target node identifier (required)
     * @param {string} [synapseData.edgeName] - Edge name
     * @param {string} [synapseData.description] - Description
     * @param {number} [synapseData.weight] - Weight
     * @param {number} [synapseData.delayMs] - Delay in milliseconds
     * @param {Array|Object} [synapseData.attributes] - Attributes
     * @param {Object} [synapseData.synapseSettings] - Synapse settings
     * @param {string} [synapseData.neuronTemplateId] - Neuron template ID
     * @returns {Promise<Object>}
     */
    createSynapse: async (synapseData) => {
      if (!synapseData.graphId) {
        throw new Error('graphId is required to create synapse');
      }
      if (!synapseData.sourceNodeId) {
        throw new Error('sourceNodeId is required to create synapse');
      }
      if (!synapseData.targetNodeId) {
        throw new Error('targetNodeId is required to create synapse');
      }
      const response = await apiClient.post('/edges', synapseData);
      return response.data;
    },
  };
};

export default createSynapseService;

